import type { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { AppointmentType, Country } from '../constants'
import type { ResourceType } from '../constants'
import { Resource } from './resource'

interface RecommendationItem {
  resource: RecommendationResource
  provider: RecommendationProvider
}

interface RecommendationResource {
  id: string
  gid: string
  email: string
  country: Country
  name: string
  shopId: string
  isActive: boolean
  timezone: string
  deliveryModels: DeliveryModel[]
  resourceType: ResourceType
  createdAt: string
  updatedAt: string
}

interface RecommendationProvider {
  country: string
  createdAt: string
  email: string
  familyName: string
  gid: string
  givenName: string
  id: string
  partnerEmployeeId: string
  salesforceId: string
  updatedAt: string
  shopId: string
}

enum EntityType {
  CUSTOMER = 'CUSTOMER',
  PROVIDER = 'PROVIDER',
  USER = 'USER',
  RESOURCE_TYPE = 'RESOURCE_TYPE',
  RESOURCE = 'RESOURCE',
  STATE = 'STATE',
}

const toResource = (recommendationResource: RecommendationResource): Resource => {
  const { id, gid, email, name, timezone, country, resourceType, createdAt, updatedAt, shopId, deliveryModels } = recommendationResource

  return {
    created_at: new Date(createdAt),
    country,
    email,
    gid,
    id,
    name,
    shop_id: shopId,
    timezone,
    type: resourceType,
    updated_at: new Date(updatedAt),
    delivery_models: deliveryModels,
  }
}

enum IssueCode {
  LICENSE_EXPIRED = 'LICENSE_EXPIRED',
  LICENSE_NOT_FOUND = 'LICENSE_NOT_FOUND',
  USER_IS_NOT_PROVIDER = 'USER_IS_NOT_PROVIDER',
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  OPPORTUNITY_NOT_FOUND = 'OPPORTUNITY_NOT_FOUND',
  APPOINTMENT_NOT_FOUND = 'APPOINTMENT_NOT_FOUND',
  INVALID_JOURNEY = 'INVALID_JOURNEY',
  COUNTRY_NOT_AVAILABLE = 'COUNTRY_NOT_AVAILABLE',
  PACKAGE_TYPE_MISMATCH = 'PACKAGE_TYPE_MISMATCH',
  APPOINTMENT_TYPE_NOT_AVAILABLE_IN_STATE = 'APPOINTMENT_TYPE_NOT_AVAILABLE_IN_STATE',
}

interface Entity {
  type: EntityType
  value: string
}

interface RecommendationIssue {
  rule: string
  code: IssueCode
  entity: Entity
}

interface Recommendation {
  gid?: string
  traceId: string
  providers: RecommendationItem[]
  recommendationIssuesSummary: RecommendationIssue[]
}

interface RecommendationResponse {
  data: Recommendation
}

interface RecommendationCreateOneRequest {
  appointmentType?: AppointmentType
  customerSalesforceId?: string
  country: string
  state: string
  insuranceCompany?: string
  journey: string
  /** Used to restrict provider recommendations to a specific sales team */
  salesTeamGid?: string
  /** Used to restrict provider recommendations to a specific shop */
  shopSalesforceId?: string
}

export { EntityType, IssueCode, toResource }

export type { Recommendation, RecommendationCreateOneRequest, RecommendationIssue, RecommendationResource, RecommendationResponse }
